import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RedeemService from '../services/RedeemService';
import API from '../services/API';
import Select from 'react-select';

const RedeemCodeForm = ({ userInfo }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // State management
    const [formData, setFormData] = useState({
        scratchCode: '',
        productSerial: '',
        product: '',
        purchasePrice: ''
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [products, setProducts] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isLoadingProducts, setIsLoadingProducts] = useState(false);
    const [scratchCodeValidation, setScratchCodeValidation] = useState({
        isValid: null,
        isChecking: false,
        message: ''
    });
    const [serialValidation, setSerialValidation] = useState({
        isValid: null,
        isChecking: false,
        message: ''
    });
    
    // Latest user info state
    const [latestUserInfo, setLatestUserInfo] = useState({
        id: null,
        name: null,
        loading: false,
        error: null
    });

    // Get URL parameters
    const urlParams = new URLSearchParams(location.search);
    const token = urlParams.get('token');
    const phoneNumber = urlParams.get('phoneNumber');
    const mainUrl = urlParams.get('mainUrl');
    const resultUrl = urlParams.get('resultUrl');

    // Function to fetch latest user information
    const fetchLatestUserInfo = useCallback(async (phoneNumber) => {
        if (!phoneNumber) {
            return;
        }

        setLatestUserInfo(prev => ({ ...prev, loading: true, error: null }));

        try {
            const result = await API.checkUserExists(phoneNumber);
            console.log('Latest user info result:', result);
            
            if (result.exists && result.userInfo) {
                setLatestUserInfo({
                    id: result.userInfo.id || result.userInfo.userId,
                    name: result.userInfo.name || result.userInfo.fullName || 
                          (result.userInfo.firstName && result.userInfo.lastName ? 
                           `${result.userInfo.firstName} ${result.userInfo.lastName}`.trim() : ''),
                    loading: false,
                    error: null
                });
            } else {
                setLatestUserInfo({
                    id: null,
                    name: null,
                    loading: false,
                    error: 'Không tìm thấy thông tin user'
                });
            }
        } catch (error) {
            console.error('Error fetching latest user info:', error);
            setLatestUserInfo({
                id: null,
                name: null,
                loading: false,
                error: 'Không thể tải thông tin user'
            });
        }
    }, []);

    // Load products on component mount
    useEffect(() => {
        loadProducts();
    }, []);

    // Fetch latest user info on component mount
    useEffect(() => {
        const userPhoneNumber = userInfo?.phoneNumber || phoneNumber;
        if (userPhoneNumber) {
            fetchLatestUserInfo(userPhoneNumber);
        }
    }, [fetchLatestUserInfo, userInfo?.phoneNumber, phoneNumber]);

    // Debounced serial validation - only validate serial when input
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (formData.productSerial.trim().length > 0) {
                validateSerialNumber(formData.productSerial.trim());
            } else {
                setSerialValidation({
                    isValid: null,
                    isChecking: false,
                    message: ''
                });
            }
        }, 1000); // 1 second delay

        return () => clearTimeout(timeoutId);
    }, [formData.productSerial]);

    // Debounced scratch code validation - validate both scratch code and serial when input
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (formData.scratchCode.trim().length > 0) {
                validateScratchCode(formData.scratchCode.trim());
                // Also re-validate serial if it exists
                if (formData.productSerial.trim().length > 0) {
                    validateSerialWithScratchCode(formData.scratchCode.trim(), formData.productSerial.trim());
                }
            } else {
                setScratchCodeValidation({
                    isValid: null,
                    isChecking: false,
                    message: ''
                });
                // Reset serial validation when scratch code is empty
                if (formData.productSerial.trim().length > 0) {
                    validateSerialNumber(formData.productSerial.trim());
                }
            }
        }, 1000); // 1 second delay

        return () => clearTimeout(timeoutId);
    }, [formData.scratchCode, formData.productSerial]);


    const loadProducts = async () => {
        setIsLoadingProducts(true);
        try {
            const data = await RedeemService.getProducts();
            const formattedProducts = data.products.map(product => ({
                value: product.id,
                label: product.name, // Since name and description are the same (tenSanPham)
                price: product.price || 0, // Include price for auto-population
                ...product
            })).sort((a, b) => (parseInt(a.stt) || 0) - (parseInt(b.stt) || 0)); // Sort by stt in ascending order
            setProducts(formattedProducts);
        } catch (error) {
            console.error('Error loading products:', error);
            setProducts([]);
        } finally {
            setIsLoadingProducts(false);
        }
    };

    const validateSerialNumber = async (serialNumber) => {
        setSerialValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            // For serial-only validation, we might need a different API endpoint
            // For now, using the same endpoint but only with serial
            const response = await RedeemService.checkScratchCode('', serialNumber);
            const result = response.data;
            console.log('validateSerialNumber result: ', result)
            // Assuming the API returns success: true/false or similar structure
            const isValid = result.success || result.valid || result.isValid || false;
            
            setSerialValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? 'Serial sản phẩm hợp lệ' : 'Serial sản phẩm không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating serial number:', error);
            setSerialValidation({
                isValid: false,
                isChecking: false,
                message: 'Serial sản phẩm không hợp lệ'
            });
        }
    };

    const validateScratchCode = async (scratchCode) => {
        setScratchCodeValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            const response = await RedeemService.checkScratchCode(scratchCode);
            const result = response.data;
            console.log('checkScratchCode result: ', result)
            // Assuming the API returns success: true/false or similar structure
            const isValid = result.success || result.valid || result.isValid || false;
            
            setScratchCodeValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? '' : 'Mã phủ cào không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating scratch code:', error);
            setScratchCodeValidation({
                isValid: false,
                isChecking: false,
                message: 'Mã phủ cào không hợp lệ'
            });
        }
    };

    const validateSerialWithScratchCode = async (scratchCode, serialNumber) => {
        setSerialValidation({
            isValid: null,
            isChecking: true,
            message: ''
        });

        try {
            const response = await RedeemService.checkScratchCode(scratchCode, serialNumber);
            const result = response.data;
            console.log('validateSerialWithScratchCode result: ', result)
            // Assuming the API returns success: true/false or similar structure
            const isValid = result.success || result.valid || result.isValid || false;
            
            setSerialValidation({
                isValid: isValid,
                isChecking: false,
                message: isValid ? 'Serial sản phẩm hợp lệ' : 'Serial sản phẩm không hợp lệ'
            });
        } catch (error) {
            console.error('Error validating serial with scratch code:', error);
            setSerialValidation({
                isValid: false,
                isChecking: false,
                message: 'Serial sản phẩm không hợp lệ'
            });
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProductChange = (selectedOption) => {
        setSelectedProduct(selectedOption);
        setFormData(prev => ({
            ...prev,
            product: selectedOption ? selectedOption.value : '',
            purchasePrice: selectedOption ? RedeemService.formatPrice(selectedOption.price.toString()) : ''
        }));
    };

    const showMessage = (text, type) => {
        setMessage({ text, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 5000);
    };

    const validateForm = () => {
        if (!formData.scratchCode.trim()) {
            showMessage('Vui lòng nhập mã phủ cào', 'error');
            return false;
        }
        if (scratchCodeValidation.isValid === false) {
            showMessage('Mã phủ cào không hợp lệ', 'error');
            return false;
        }
        if (scratchCodeValidation.isChecking) {
            showMessage('Đang kiểm tra mã phủ cào, vui lòng đợi...', 'error');
            return false;
        }
        if (!formData.productSerial.trim()) {
            showMessage('Vui lòng nhập serial sản phẩm', 'error');
            return false;
        }
        if (serialValidation.isValid === false) {
            showMessage('Serial sản phẩm không hợp lệ', 'error');
            return false;
        }
        if (serialValidation.isChecking) {
            showMessage('Đang kiểm tra serial sản phẩm, vui lòng đợi...', 'error');
            return false;
        }
        if (!selectedProduct) {
            showMessage('Vui lòng chọn sản phẩm', 'error');
            return false;
        }
        if (!formData.purchasePrice.replace(/[^\d]/g, '')) {
            showMessage('Vui lòng chọn sản phẩm để có giá mua', 'error');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setMessage({ text: '', type: '' });

        try {

            console.log('Submitting formData:', formData);
            // Prepare data for API call
            const submitData = {
                productId: selectedProduct ? selectedProduct.value : '',
                productName: selectedProduct ? selectedProduct.label : '',
                serialNumber: formData.productSerial,
                price: Number(formData.purchasePrice.replace(/[^\d]/g, '')),
                scratchCode: formData.scratchCode,
            };

            console.log('Submitting redeem data 1:', submitData);

            // Submit to API
            const result = await RedeemService.submitRedeemCode(submitData, token);

            if (result.data.success) {
                // Show success message briefly before redirecting
                showMessage('Mã phủ cào đã được xác nhận thành công!', 'success');

                // Redirect to result page after a short delay
                setTimeout(() => {
                    const resultParams = new URLSearchParams({
                        token: token || '',
                        phoneNumber: phoneNumber || '',
                        mainUrl: mainUrl || '',
                        resultUrl: resultUrl || '',
                        scratchCode: formData.scratchCode,
                        productSerial: formData.productSerial,
                        product: selectedProduct ? selectedProduct.value : '',
                        purchasePrice: formData.purchasePrice
                    });
                    navigate(`/redeem-code-result?${resultParams.toString()}`);
                }, 1000); // 1.5 second delay to show success message
            } else {
                showMessage(result.data.message || 'Có lỗi xảy ra, vui lòng thử lại', 'error');
            }

        } catch (error) {
            console.error('Error submitting redeem data:', error);
            
            // Extract error message from API response if available
            let errorMessage = 'Có lỗi xảy ra, vui lòng thử lại';
            
            if (error.response && error.response.data) {
                // If API returns error message in response
                errorMessage = error.response.data.message || error.response.data.error || errorMessage;
            } else if (error.message) {
                // If it's a network error or other error with message
                errorMessage = error.message;
            }
            
            showMessage(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="gradient-bg min-h-screen flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md animate-fade-in">
                <div className="text-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#277f4d' }}>
                        <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                    </div>
                    <h1 className="text-2xl font-bold text-gray-800">Nhập mã phủ cào</h1>
                    {latestUserInfo.loading ? (
                        <p className="text-gray-600 mt-2">Đang tải thông tin user...</p>
                    ) : latestUserInfo.error ? (
                        userInfo?.name && (
                            <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{userInfo.id}-{userInfo.name}</span></p>
                        )
                    ) : latestUserInfo.id && latestUserInfo.name ? (
                        <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{latestUserInfo.id}-{latestUserInfo.name}</span></p>
                    ) : userInfo?.name && (
                        <p className="text-gray-600 mt-2"><span className="font-semibold" style={{ color: '#277f4d' }}>{userInfo.id}-{userInfo.name}</span></p>
                    )}
                    
                    {/* Danh sách đã nhập button */}
                    <button
                        type="button"
                        onClick={() => {
                            const resultParams = new URLSearchParams({
                                token: token || '',
                                phoneNumber: phoneNumber || '',
                                mainUrl: mainUrl || '',
                                resultUrl: resultUrl || ''
                            });
                            navigate(`/redeem-code-result?${resultParams.toString()}`);
                        }}
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200"
                        style={{ 
                            color: '#277f4d', 
                            backgroundColor: '#f0f9f4', 
                            borderColor: '#d1fae5' 
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.backgroundColor = '#dcfce7';
                            e.target.style.borderColor = '#bbf7d0';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.backgroundColor = '#f0f9f4';
                            e.target.style.borderColor = '#d1fae5';
                        }}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
                        </svg>
                        Danh sách đã nhập
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Serial sản phẩm *
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="productSerial"
                                value={formData.productSerial}
                                onChange={handleInputChange}
                                required
                                placeholder="Nhập serial sản phẩm"
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    serialValidation.isValid === true
                                        ? 'border-green-500 bg-green-50'
                                        : serialValidation.isValid === false
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                }`}
                                style={{'--tw-ring-color': '#277f4d'}}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {serialValidation.isChecking && (
                                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                                         style={{borderColor: '#277f4d', borderTopColor: 'transparent'}}></div>
                                )}
                                {serialValidation.isValid === true && !serialValidation.isChecking && (
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M5 13l4 4L19 7"></path>
                                    </svg>
                                )}
                                {serialValidation.isValid === false && !serialValidation.isChecking && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                        {serialValidation.message && (
                            <p className={`mt-1 text-sm ${
                                serialValidation.isValid === true ? 'text-green-600' : 'text-red-600'
                            }`}>{serialValidation.message}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Mã phủ cào * (Mã số bên dưới lớp cào)
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                name="scratchCode"
                                value={formData.scratchCode}
                                onChange={handleInputChange}
                                required
                                placeholder="Nhập mã phủ cào"
                                className={`w-full px-4 py-3 pr-12 border rounded-lg focus:ring-2 focus:border-transparent ${
                                    scratchCodeValidation.isValid === true
                                        ? 'border-green-500 bg-green-50'
                                        : scratchCodeValidation.isValid === false
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300'
                                }`}
                                style={{'--tw-ring-color': '#277f4d'}}
                            />
                            <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                                {scratchCodeValidation.isChecking && (
                                    <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                                         style={{borderColor: '#277f4d', borderTopColor: 'transparent'}}></div>
                                )}
                                {scratchCodeValidation.isValid === true && !scratchCodeValidation.isChecking && (
                                    <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M5 13l4 4L19 7"></path>
                                    </svg>
                                )}
                                {scratchCodeValidation.isValid === false && !scratchCodeValidation.isChecking && (
                                    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor"
                                         viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                              d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                )}
                            </div>
                        </div>
                        {scratchCodeValidation.message && (
                            <p className="mt-1 text-sm text-red-600">{scratchCodeValidation.message}</p>
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Sản phẩm *
                        </label>
                        <Select
                            value={selectedProduct}
                            onChange={handleProductChange}
                            options={products}
                            isLoading={isLoadingProducts}
                            placeholder="Gõ để tìm kiếm sản phẩm..."
                            isClearable
                            isSearchable
                            noOptionsMessage={() => "Không tìm thấy sản phẩm"}
                            loadingMessage={() => "Đang tải..."}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            styles={{
                                control: (provided, state) => ({
                                    ...provided,
                                    minHeight: '48px',
                                    border: state.isFocused
                                        ? '2px solid #277f4d'
                                        : '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    boxShadow: state.isFocused
                                        ? '0 0 0 3px rgba(39, 127, 77, 0.1)'
                                        : 'none',
                                    '&:hover': {
                                        border: '1px solid #9ca3af'
                                    }
                                }),
                                placeholder: (provided) => ({
                                    ...provided,
                                    color: '#9ca3af'
                                }),
                                option: (provided, state) => ({
                                    ...provided,
                                    backgroundColor: state.isSelected
                                        ? '#277f4d'
                                        : state.isFocused
                                            ? '#f3f4f6'
                                            : 'white',
                                    color: state.isSelected ? 'white' : '#374151',
                                    '&:hover': {
                                        backgroundColor: state.isSelected ? '#277f4d' : '#f3f4f6'
                                    }
                                })
                            }}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Giá mua *
                        </label>
                        <input
                            type="text"
                            name="purchasePrice"
                            value={formData.purchasePrice}
                            readOnly
                            placeholder="Giá sẽ được tự động điền khi chọn sản phẩm"
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                            style={{'--tw-ring-color': '#277f4d'}}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading || scratchCodeValidation.isChecking || serialValidation.isChecking}
                        className="w-full text-white py-3 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        style={{backgroundColor: '#277f4d'}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#1f5f3a'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#277f4d'}
                    >
                        <span>
                            {loading
                                ? 'Đang xử lý...'
                                : (scratchCodeValidation.isChecking || serialValidation.isChecking)
                                    ? 'Đang kiểm tra...'
                                    : 'Xác nhận'
                            }
                        </span>
                        {(loading || scratchCodeValidation.isChecking || serialValidation.isChecking) && (
                            <div
                                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        )}
                    </button>
                </form>

                {/* Error/Success Message */}
                {message.text && (
                    <div className="mt-4">
                        <div className={`p-4 rounded-lg ${
                            message.type === 'error'
                                ? 'bg-red-50 border border-red-200 text-red-800'
                                : 'bg-green-50 border border-green-200 text-green-800'
                        }`}>
                            {message.text}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default RedeemCodeForm;
